/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmailWithTooltip from "@stoneage/App/Components/Common/EmailWithTooltip";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleStyle = "titleStyle",
    linkText = "linkText",
    linkUrl = "linkUrl",
    colorTheme = "colorTheme",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.linkText]: string;
        [fields.linkUrl]: LinkFieldValue;
        [fields.colorTheme]: "light" | "dark";
        [fields.openInNewTab]: boolean;
    };
}

type Props = OwnProps & HasHistory & PropFromRedux;

export interface MySalesRepStyles {
    widgetContainer?: InjectableCss;
    infoWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    infoItem?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const mySalesRepStyles: MySalesRepStyles = {
    widgetContainer: {
        css: css`
            height: 100%;
            padding: 20px;
            box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
            border-radius: 2px;
            min-height: 225px;
            display: flex;
            flex-direction: column;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    infoItem: {
        css: css`
            display: block;
        `,
    },
    link: {
        css: css`
            span {
                display: flex;
                align-items: center;
            }
        `,
    },
    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    linkWrapper: {
        css: css`
            margin-top: auto;
        `,
    },
};

const MySalesRep = ({ fields, isAuthenticated, salesPersonPhone, salesPersonName, salesPersonEmail }: Props) => {
    const styles = mySalesRepStyles;

    if (!isAuthenticated) {
        return <></>;
    }

    const { title, titleStyle, linkText, linkUrl, openInNewTab, colorTheme } = fields;

    const link = useGetLink(linkUrl);

    const getTextColor = () => (colorTheme === "light" ? "common.black" : "common.white");

    return (
        <StyledWrapper {...styles.widgetContainer}>
            {title && (
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTextColor()}>
                    {title}
                </Typography>
            )}
            <StyledWrapper translate="no" {...styles.infoWrapper}>
                {salesPersonName && (
                    <Typography
                        {...styles.infoItem}
                        color={getTextColor()}
                        css={css`
                            ${styles.infoItem?.css};
                            margin-bottom: 12px;
                        `}
                    >
                        {salesPersonName}
                    </Typography>
                )}
                {salesPersonPhone && (
                    <Typography {...styles.infoItem} color={getTextColor()}>
                        <a href={`tel:${salesPersonPhone}`}>{salesPersonPhone}</a>
                    </Typography>
                )}
                {salesPersonEmail && (
                    <Typography
                        {...styles.infoItem}
                        color={getTextColor()}
                        css={css`
                            ${styles.infoItem?.css};
                            font-size: 14px;
                        `}
                    >
                        <EmailWithTooltip email={salesPersonEmail}></EmailWithTooltip>
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.linkWrapper}>
                <Link href={link.url} target={openInNewTab ? "_blank" : ""} {...styles.link} color={getTextColor()} id="supportservices">
                    {linkText || link.title}
                    <Icon
                        src="ChevronRight"
                        size={14}
                        color={getTextColor()}
                        css={css`
                            margin-left: 8px;
                        `}
                    ></Icon>
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        salesPersonEmail: state.context.session.properties.salesPersonEmail || "",
        salesPersonName: state.context.session.properties.salesPersonName || "",
        salesPersonPhone: state.context.session.properties.salesPersonPhone || "",
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const connector = connect(mapStateToProps);
type PropFromRedux = ConnectedProps<typeof connector>;

const mySalesRep: WidgetModule = {
    component: connector(withHistory(MySalesRep)),
    definition: {
        displayName: "My Sales Rep",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.linkText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.linkUrl,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
        ],
    },
};

export default mySalesRep;
